import React, { useMemo, useCallback, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import axios from 'axios'
import { ContentUpload, ProgressBarContainer } from './style'
import { AiOutlineCloudUpload } from 'react-icons/ai'
import UploadProgressBar from '../UploadProgressBar'
import Swal from 'sweetalert2'
import { getSignedUrl } from '../../../../services/files'

interface IPropsUploadSamba {
	setReference: React.Dispatch<React.SetStateAction<string>>
}

interface IFile extends File {
	path: string
}

const TAMANHO_MAXIMO = 19 * 1024 * 1024 * 1024 // aproximadamente 20GB
const LIMITE_ARQUIVOS = 1

const baseStyle = {
	cursor: 'pointer',
	flex: 1,
	display: 'flex',
	alignItems: 'center',
	padding: '0px',
	borderWidth: 2,
	borderRadius: 2,
	color: '#bdbdbd',
	outline: 'none',
	height: '100%',
	width: '50%',
	transition: 'border .24s ease-in-out',
}

const focusedStyle = {
	borderColor: '#2196f3',
}

const acceptStyle = {
	borderColor: '#00e676',
}

const rejectStyle = {
	borderColor: '#ff1744',
}

const UploadSamba: React.FC<IPropsUploadSamba> = ({ setReference }) => {
	const [uploadProgress, setUploadProgress] = useState(0)

	function InputDropzone(props: any) {
		const onDrop = useCallback(async (acceptedFiles) => {
			let pathIndex: string = ''

			Swal.fire({
				title: 'Iniciando Upload',
				text: 'Upload de conteúdo iniciado!',
				timer: 2000,
			})

			let countFilesUpload: number = 0
			acceptedFiles.forEach(async (file: IFile, index: number) => {
				const uploadUrl = `https://upload.spalla.io/v1/videos/upload`
				const tentativasTotal = 5

				const nomeDoArquivo = file.name
				const formPayload = new FormData()
				formPayload.append('file', file, nomeDoArquivo)

				let tentativa = 0
				while (true) {
					tentativa++

					try {
						const res = await axios({
							url: uploadUrl,
							method: 'post',
							headers: {
								Authorization: `Bearer ${process.env.REACT_APP_SAMBA_ACCESS_TOKEN}`,
							},
							data: formPayload,
							onUploadProgress: (progressEvent) => {
								const percentageProgress = Math.floor(
									(progressEvent.loaded / progressEvent.total) * 100
								)
								setUploadProgress(percentageProgress)
							},
						})

						if (res.status === 202) {
							setUploadProgress(0)
							Swal.fire({
								title: 'Sucesso!',
								text: 'Upload realizado com sucesso! Aguarde o tempo de encoding que pode durar até 10 min, após isso seu vídeo estará disponível para cadastro.',
								timer: 4000,
							})
							return true // tudo certo, upload concluído. sai do loopPrincipal
						} else if (res.status >= 500) {
							console.error('Erro no servidor para', nomeDoArquivo, 'Tentativa ', tentativa, res)
							continue // tenta novamente o mesmo chunk
						} else {
							console.error('Erro no upload para', nomeDoArquivo, 'Tentativa ', tentativa, res)
							break // erro na requisição (status 4XX), sai do loopPrincipal
						}
					} catch (err) {
						console.error('Erro no upload para', nomeDoArquivo, 'Tentativa ', tentativa, err)
					}

					if (tentativa >= tentativasTotal) {
						Swal.fire({
							title: 'Erro!',
							icon: 'error',
							text: 'Erro no upload!',
						})
						break // sai do loopPrincipal
					}
				}

				let contentType: string = file.type
				if (!contentType) {
					contentType = file.path.split('.')[1] === 'xsd' ? 'application/xml' : 'application/octet-stream'
				}

				if (file.path.includes('/res/index.html') || file.path.includes('index.html')) {
					pathIndex = `${process.env.REACT_APP_ASSETS_SCORM}/${encodeURI(file.path)}`
				}

				const { uploadURL } = await getSignedUrl(contentType, file.path)
				const reader = new FileReader()

				reader.onload = async (data: any) => {
					const dataFileUrl = data.target.result
					let binary = atob(dataFileUrl.split(',')[1])
					let array = []
					for (let i = 0; i < binary.length; i++) {
						array.push(binary.charCodeAt(i))
					}
					let blobData = new Blob([new Uint8Array(array)], {
						type: contentType,
					})

					await fetch(uploadURL, {
						method: 'PUT',
						body: blobData,
						headers: {
							'Access-Control-Allow-Origin': '*',
						},
					})
					countFilesUpload = countFilesUpload + 1
					const progress = (countFilesUpload / acceptedFiles.length) * 100
					setUploadProgress(progress)
					if (countFilesUpload === acceptedFiles.length) {
						setReference(pathIndex)
						setUploadProgress(0)
						Swal.fire({
							title: 'Sucesso!',
							icon: 'success',
							text: 'Upload do scorm realizado com sucesso!',
						})
					}
				}
				reader.readAsDataURL(file)
			})
		}, [])

		const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } = useDropzone({
			accept: `video/*, audio/*`,
			maxFiles: LIMITE_ARQUIVOS,
			maxSize: TAMANHO_MAXIMO,
			onDrop,
			noKeyboard: true,
		})

		const style = useMemo(
			() => ({
				...baseStyle,
				...(isFocused ? focusedStyle : {}),
				...(isDragAccept ? acceptStyle : {}),
				...(isDragReject ? rejectStyle : {}),
			}),
			[isFocused, isDragAccept, isDragReject]
		)

		return (
			<div {...getRootProps({ style })}>
				<input {...getInputProps()} />
				<ContentUpload>
					<AiOutlineCloudUpload size={128} />
					<p>Fazer Upload de Conteúdo</p>
					{uploadProgress > 0 ? (
						<ProgressBarContainer>
							<UploadProgressBar progress={uploadProgress} />
						</ProgressBarContainer>
					) : (
						<></>
					)}
				</ContentUpload>
			</div>
		)
	}

	return <InputDropzone />
}

export default UploadSamba
